export type GrantRadarInterest = { id?: string; name: string; details?: string | null; active: boolean };

export type GrantRadarProfile = {
  applicant_name: string | null;
  community_details: string | null;
  funding_needs: string | null;
  common_disqualifiers: string | null;
  interests: GrantRadarInterest[];
  project_size: string | null;
  project_timing: string | null;
  readiness: string | null;
  match_capacity: string | null;
  exclusions: string | null;
  previous_awards: string | null;
  preferred_sources: string | null;
  updated_at: string;
  ready: boolean;
};

export type GrantRadarSource = { title?: string; url: string; kind: string; fetched_at?: string };

export type GrantRadarRunStatus = "queued" | "running" | "completed" | "failed";

export type GrantRadarRun = {
  id: number;
  status: GrantRadarRunStatus;
  error_message: string | null;
  new_count: number;
  finished_at: string | null;
  created_at: string;
  activity: { tool: string; detail?: string | null; at: string }[];
};

export type GrantRadarCriterion = {
  criterion: string;
  requirement: string;
  profile_fact?: string;
  status: "met" | "unknown" | "conflicting";
  sources: GrantRadarSource[];
};

export type GrantRadarDecision = "saved" | "does_not_fit" | "archived";

export type GrantRadarFinding = {
  id: number;
  funder: string;
  program_name: string;
  round_label: string | null;
  description: string | null;
  official_url: string | null;
  funding_type: string;
  award_amount: string | null;
  award_amount_basis: string | null;
  match_requirement: string | null;
  geography_scope: string | null;
  geography_states: string[];
  geography_detail: string | null;
  availability: "open" | "rolling" | "forecast" | "closed" | "prior_round" | "unknown";
  deadline_on: string | null;
  deadline_text: string | null;
  criteria: GrantRadarCriterion[];
  sources: GrantRadarSource[];
  first_seen_at: string;
  decision: GrantRadarDecision | null;
  decision_reason: string | null;
  decided_at: string | null;
};

export type GrantRadarState = {
  options: { project_sizes: string[]; project_timings: string[] };
  profile: GrantRadarProfile | null;
  latest_run: GrantRadarRun | null;
  last_successful_run: GrantRadarRun | null;
  findings: GrantRadarFinding[];
};

export type GrantRadarProfilePayload = Partial<Omit<GrantRadarProfile, "updated_at" | "ready">>;
export type GrantRadarFindingPayload = { decision: GrantRadarDecision | null; decision_reason?: string | null };

export class GrantRadarError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

function base(municipalityId: number) {
  return `/api/v1/municipalities/${municipalityId}/grant_radar`;
}

async function request<T>(url: string, init: RequestInit = {}): Promise<T> {
  const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
  const response = await fetch(url, {
    credentials: "include",
    ...init,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrf,
      "X-Time-Zone": Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...init.headers,
    },
  });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const errors = body?.errors;
    const message = Array.isArray(errors) && errors.length > 0 ? errors.join(" ") : "Grant Radar could not complete that request. Try again.";
    throw new GrantRadarError(message, response.status);
  }
  return body as T;
}

export function fetchGrantRadar(municipalityId: number) {
  return request<GrantRadarState>(base(municipalityId));
}

export function saveGrantRadarProfile(municipalityId: number, profile: GrantRadarProfilePayload) {
  return request<GrantRadarState>(`${base(municipalityId)}/profile`, { method: "PUT", body: JSON.stringify({ profile }) });
}

export function startGrantRadarRun(municipalityId: number) {
  const time_zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return request<GrantRadarState>(`${base(municipalityId)}/runs`, { method: "POST", body: JSON.stringify({ time_zone }) });
}

export function updateGrantRadarFinding(municipalityId: number, findingId: number, finding: GrantRadarFindingPayload) {
  return request<{ finding: GrantRadarFinding }>(`${base(municipalityId)}/findings/${findingId}`, {
    method: "PATCH",
    body: JSON.stringify({ finding }),
  });
}
